import { useState, useEffect } from 'react';
import AdminSidebar from '../../components/feature/AdminSidebar';
import { supabase } from '../../lib/supabase';

interface Fan {
  id: string;
  name: string;
  avatar: string;
  email: string;
  subscriptions: number;
  totalSpent: number;
  balance: number;
  status: 'active' | 'suspended';
  joinDate: string;
}

export default function AdminFans() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFan, setSelectedFan] = useState<Fan | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [fans, setFans] = useState<Fan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFans();
  }, []);

  const loadFans = async () => {
    try {
      setLoading(true);
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'fan')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (profiles) {
        const fansWithStats = await Promise.all(
          profiles.map(async (profile) => {
            // 구독 수
            const { count: subscriptionCount } = await supabase
              .from('subscriptions')
              .select('*', { count: 'exact' })
              .eq('fan_id', profile.id)
              .eq('status', 'active');

            // 총 지출
            const { data: transactions } = await supabase
              .from('transactions')
              .select('amount')
              .eq('from_user_id', profile.id)
              .in('type', ['subscription', 'tip', 'charge']);

            const totalSpent = transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;

            return {
              id: profile.id,
              name: profile.full_name || profile.username || '익명',
              avatar: profile.avatar_url || `https://public.readdy.ai/ai/img_res/d141d985860433035fb16920503d6e47.jpg${profile.id}&orientation=squarish`,
              email: profile.email || '',
              subscriptions: subscriptionCount || 0,
              totalSpent,
              balance: profile.balance || 0,
              status: (profile.status as 'active' | 'suspended') || 'active',
              joinDate: new Date(profile.created_at).toLocaleDateString('ko-KR'),
            };
          })
        );
        setFans(fansWithStats);
      }
    } catch (error) {
      console.error('팬 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFans = fans.filter(fan => {
    const matchesSearch = fan.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         fan.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || fan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-teal-100 text-teal-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return '활성';
      case 'suspended': return '정지';
      default: return status;
    }
  };

  const handleSuspend = async (fan: Fan) => {
    if (confirm(`${fan.name} 팬을 정지하시겠습니까?`)) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ status: 'suspended' })
          .eq('id', fan.id);

        if (error) throw error;

        alert('팬이 정지되었습니다.');
        setShowDetailModal(false);
        loadFans();
      } catch (error) {
        console.error('정지 실패:', error);
        alert('정지에 실패했습니다.');
      }
    }
  };

  const handleActivate = async (fan: Fan) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: 'active' })
        .eq('id', fan.id);

      if (error) throw error;

      alert(`${fan.name} 팬을 활성화했습니다.`);
      setShowDetailModal(false);
      loadFans();
    } catch (error) {
      console.error('활성화 실패:', error);
      alert('활성화에 실패했습니다.');
    }
  };

  const openDetailModal = (fan: Fan) => {
    setSelectedFan(fan);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50">
      <AdminSidebar />
      
      <div className="lg:ml-64 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* 헤더 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">팬 관리</h1>
            <p className="text-gray-600">총 {fans.length}명의 팬이 등록되어 있습니다</p>
          </div>

          {/* 검색 및 필터 */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="팬 검색..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="all">전체 상태</option>
                <option value="active">활성</option>
                <option value="suspended">정지</option>
              </select>
            </div>
          </div>

          {/* 팬 테이블 */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-teal-50 to-blue-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">팬</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">이메일</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">구독 수</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">총 지출</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">잔액</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">상태</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">가입일</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">액션</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFans.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        {searchTerm || statusFilter !== 'all' ? '검색 결과가 없습니다' : '아직 등록된 팬이 없습니다'}
                      </td>
                    </tr>
                  ) : (
                    filteredFans.map((fan) => (
                      <tr key={fan.id} className="hover:bg-gray-50 transition-all">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full overflow-hidden">
                              <img src={fan.avatar} alt={fan.name} className="w-full h-full object-cover" />
                            </div>
                            <span className="font-semibold text-gray-800">{fan.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{fan.email}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-800">{fan.subscriptions}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-800">₩{fan.totalSpent.toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">₩{fan.balance.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(fan.status)}`}>
                            {getStatusLabel(fan.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{fan.joinDate}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => openDetailModal(fan)}
                            className="px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-lg text-sm font-semibold hover:from-teal-600 hover:to-blue-600 transition-all whitespace-nowrap cursor-pointer"
                          >
                            관리
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 상세 관리 모달 */}
      {showDetailModal && selectedFan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-800">팬 상세 정보</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            {/* 내용 */}
            <div className="p-6 space-y-6">
              {/* 프로필 */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden">
                  <img src={selectedFan.avatar} alt={selectedFan.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-800">{selectedFan.name}</h4>
                  <p className="text-gray-600">{selectedFan.email}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2 ${getStatusColor(selectedFan.status)}`}>
                    {getStatusLabel(selectedFan.status)}
                  </span>
                </div>
              </div>

              {/* 통계 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-1">구독 수</p>
                  <p className="text-2xl font-bold text-gray-800">{selectedFan.subscriptions}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-1">총 지출</p>
                  <p className="text-2xl font-bold text-gray-800">₩{selectedFan.totalSpent.toLocaleString()}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-1">잔액</p>
                  <p className="text-2xl font-bold text-gray-800">₩{selectedFan.balance.toLocaleString()}</p>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-1">가입일</p>
                  <p className="text-2xl font-bold text-gray-800">{selectedFan.joinDate}</p>
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="flex gap-3">
                {selectedFan.status === 'active' && (
                  <button
                    onClick={() => handleSuspend(selectedFan)}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-forbid-line mr-2"></i>
                    정지하기
                  </button>
                )}
                {selectedFan.status === 'suspended' && (
                  <button
                    onClick={() => handleActivate(selectedFan)}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-blue-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-check-line mr-2"></i>
                    활성화하기
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}